import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  Typography,
  Box,
  Avatar,
  Button,
  CircularProgress,
  Chip,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ReceiptLong as ReceiptLongIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Business as BusinessIcon,
  QrCode as QrCodeIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { SwalConfig } from '../../utils/swalConfig';

const ExternalTransactionDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [transaction, setTransaction] = useState(null);

  useEffect(() => {
    const fetchTransactionDetail = async () => {
      try {
        setLoading(true);
        const response = await fetch(`https://apidashboard.eurekagroup.id/external-transactions/get/${id}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();
        setTransaction(result.data || result);
      } catch (error) {
        console.error('Error fetching transaction detail:', error);
        Swal.fire({
          ...SwalConfig.error,
          title: 'Error!',
          text: `Gagal memuat detail transaksi: ${error.message}`
        });
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchTransactionDetail();
    }
  }, [id]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'Rp 0';
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'COMPLETED':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  const getTypeColor = (type) => {
    switch (type?.toUpperCase()) {
      case 'USED':
        return 'error';
      case 'EARNED':
        return 'success';
      default:
        return 'info';
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: 400,
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ color: 'text.secondary' }}>
          Memuat data transaksi...
        </Typography>
      </Box>
    );
  }

  if (!transaction) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" sx={{ color: 'text.secondary' }}>
          Transaksi tidak ditemukan
        </Typography>
      </Box>
    );
  }

  const isUsed = transaction.type === 'USED';
  const pointChange = isUsed ? -transaction.point : transaction.point;

  return (
    <div className="animate-fade-in">
      <Card sx={{ boxShadow: '0 8px 32px rgba(0,0,0,0.1)', borderRadius: 3 }} className="bg-gradient-to-br from-white to-blue-50">
        <CardHeader 
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }} className="shadow-lg">
                <ReceiptLongIcon />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }} className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Detail External Transaction
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} className="text-gray-600">
                  Informasi lengkap transaksi eksternal
                </Typography>
              </Box>
            </Box>
          }
          action={
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/eureka-plus/external-transactions')}
              sx={{
                borderRadius: 2,
                px: 2,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Kembali
            </Button>
          }
          sx={{ pb: 2 }}
        />
        <Divider />
        <CardContent>
          <Grid container spacing={3}>
            {/* Header Card with Transaction Code */}
            <Grid item xs={12}>
              <Box sx={{ 
                p: 4, 
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'white', 
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'grey.200',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(147, 51, 234, 0.05) 100%)'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Avatar 
                      sx={{ 
                        width: 80, 
                        height: 80,
                        bgcolor: isUsed ? 'error.main' : 'success.main',
                        fontSize: '2rem',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                      }}
                    >
                      {isUsed ? <TrendingDownIcon /> : <TrendingUpIcon />}
                    </Avatar>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
                        {transaction.transaction_code || '-'}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        <Chip 
                          label={transaction.type || '-'}
                          color={getTypeColor(transaction.type)}
                          icon={isUsed ? <TrendingDownIcon /> : <TrendingUpIcon />}
                          sx={{ fontWeight: 600 }}
                        />
                        <Chip 
                          label={transaction.status || '-'}
                          color={getStatusColor(transaction.status)}
                          icon={<CheckCircleIcon />}
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: 'monospace', bgcolor: 'grey.100', p: 1, borderRadius: 1, display: 'inline-block' }}>
                        Order ID: {transaction.id_order}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                      Point Change
                    </Typography>
                    <Typography 
                      variant="h3" 
                      sx={{ 
                        fontWeight: 700,
                        color: isUsed ? 'error.main' : 'success.main'
                      }}
                    >
                      {isUsed ? '-' : '+'}{formatCurrency(transaction.point)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Transaction Information */}
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                p: 3, 
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'white', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                height: '100%'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <QrCodeIcon /> Informasi Transaksi
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                      Transaction Code
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'monospace', bgcolor: 'grey.100', p: 1.5, borderRadius: 1 }}>
                      {transaction.transaction_code || '-'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                      Reference Number
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {transaction.no_reference || '-'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BusinessIcon fontSize="small" /> Business Unit
                    </Typography>
                    <Chip 
                      label={`BU ${transaction.id_business_unit || '-'}`}
                      variant="outlined"
                      color="info"
                      sx={{ fontWeight: 500 }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                      User ID
                    </Typography>
                    <Chip 
                      label={transaction.id_user || '-'}
                      variant="outlined"
                      color="info"
                      sx={{ fontWeight: 500 }}
                    />
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Point Information */}
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                p: 3, 
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'white', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                height: '100%'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccountBalanceWalletIcon /> Informasi Point
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                      Point {isUsed ? 'Digunakan' : 'Diperoleh'}
                    </Typography>
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        fontWeight: 700,
                        color: isUsed ? 'error.main' : 'success.main'
                      }}
                    >
                      {isUsed ? '-' : '+'}{formatCurrency(transaction.point)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                      Point Sebelumnya
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {formatCurrency(transaction.old_point)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                      Point Setelah Transaksi
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {formatCurrency(transaction.last_point)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Timeline Information */}
            <Grid item xs={12}>
              <Box sx={{ 
                p: 3, 
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'white', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ScheduleIcon /> Informasi Waktu
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ 
                      p: 2, 
                      bgcolor: 'grey.50', 
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'grey.200'
                    }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                        Date Created
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {formatDate(transaction.date_created)}
                      </Typography>
                    </Box>
                  </Grid>
                  {transaction.date_updated && (
                    <Grid item xs={12} md={4}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: 'grey.50', 
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'grey.200'
                      }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                          Date Updated
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {formatDate(transaction.date_updated)}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {transaction.expired_date && (
                    <Grid item xs={12} md={4}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: 'grey.50', 
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'grey.200'
                      }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                          Expired Date
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {formatDate(transaction.expired_date)}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </div>
  );
};

export default ExternalTransactionDetailPage;
